import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { ConversationSummary } from '../../../domain/models/conversation-summary.model';

export type ConversationListItemAction =
  | { readonly action: 'pin' | 'removePin' | 'delete'; readonly conversationId: string }
  | { readonly action: 'rename'; readonly conversationId: string; readonly title: string };

@Component({
  selector: 'talk-to-your-data-conversation-list-item',
  templateUrl: './conversation-list-item.component.html',
  styleUrls: ['./conversation-history.component.scss', './conversation-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationListItemComponent {
  @Input() item: ConversationSummary = { conversationId: '', previewText: '', lastActivityDate: '' };
  @Input() conversationId = '';
  @Input() testId = 'conversation';
  @Input() selected = false;
  @Input() pinned = false;
  @Output() readonly conversationSelected = new EventEmitter<string>();
  @Output() readonly actionSelected = new EventEmitter<ConversationListItemAction>();
  @ViewChild('renameInput') private readonly renameInput?: ElementRef<HTMLInputElement>;
  @ViewChild('menuOrigin') menuOrigin?: CdkOverlayOrigin;
  @ViewChild('menuButton', { read: ElementRef }) private readonly menuTrigger?: ElementRef<HTMLElement>;

  readonly menuPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 8 },
    { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX: -8 },
  ];

  menuOpen = false;
  isEditing = false;
  draftTitle = '';

  toggleMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.menuOpen = !this.menuOpen;
  }

  closeMenu(): void {
    this.menuOpen = false;
  }

  onMenuKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeMenu();
      this.menuTrigger?.nativeElement.focus();
    }
  }

  onConversationSelected(): void {
    if (this.conversationId) {
      this.conversationSelected.emit(this.conversationId);
    }
  }

  onMenuAction(action: 'pin' | 'removePin' | 'rename' | 'delete'): void {
    this.menuOpen = false;

    if (action === 'rename') {
      this.beginRename();
      return;
    }

    if (this.conversationId) {
      this.actionSelected.emit({ action, conversationId: this.conversationId });
    }
  }

  onTitleInput(event: Event): void {
    this.draftTitle = (event.target as HTMLInputElement).value;
  }

  onRenameKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.saveRename();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.cancelRename();
    }
  }

  saveRename(): void {
    if (!this.isEditing) {
      return;
    }

    const title = this.draftTitle.trim();
    this.isEditing = false;

    if (this.conversationId && title && title !== (this.item.title || this.item.previewText)) {
      this.actionSelected.emit({ action: 'rename', conversationId: this.conversationId, title });
    }
  }

  cancelRename(): void {
    this.isEditing = false;
    this.draftTitle = this.item.title || this.item.previewText;
  }

  private beginRename(): void {
    this.draftTitle = this.item.title || this.item.previewText;
    this.isEditing = true;
    setTimeout(() => this.renameInput?.nativeElement.focus());
  }
}
